import {
  IQuoteServiceVariable,
  IQuoteServiceVariableData,
  IQuoteServiceVariableFieldError,
  IQuoteServiceVariableFieldErrorData,
  IServiceComputationNodeData,
  IServicePriceComputation,
  IServicePriceComputationData,
  IServiceComputationNode,
  ComputationNodeType,
  ComputationBinaryOperators,
  ComputationNodeSubtype,
  ExpressionNodeTree,
  IServiceComputationFieldError,
  IServiceComputationNodeFieldErrorData,
  IServiceComputationNodeFieldError,
  IServiceComputationFieldErrorData
} from './quoteservicecomputation.types';
export class QuoteServiceVariable implements IQuoteServiceVariable {
  id?: number;
  name = '';
  value = '';
  service: null | number = null;

  constructor(data?: IQuoteServiceVariableData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      this.name = data.name;
      this.value = data.value;
      this.service = data.service ?? null;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteServiceVariableFieldError
  implements IQuoteServiceVariableFieldError {
  name: string[] = [];
  value: string[] = [];

  constructor(data?: IQuoteServiceVariableFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }
      if (data.value) {
        this.value = data.value;
      }
    }
  }

  clearErrors() {
    this.name.splice(0, this.name.length);
    this.value.splice(0, this.value.length);
  }

  static isQuoteServiceVariableFieldError(
    data: IQuoteServiceVariableFieldErrorData
  ): boolean {
    if (data.name && Array.isArray(data.name)) {
      return true;
    }

    if (data.value && Array.isArray(data.value)) {
      return true;
    }

    return false;
  }
}

export class ServiceComputationNode implements IServiceComputationNode {
  readonly id?: number;

  order = 1;

  readonly type: ComputationNodeType = ComputationNodeType.operator;

  readonly subtype: ComputationNodeSubtype | ComputationBinaryOperators = 0;

  readonly constant: number | null = null;

  readonly days_computation: number | null = null;

  readonly variable: number | null = null;

  readonly items: string[] = [];

  readonly item_tags: number[] = [];

  constructor(data?: IServiceComputationNodeData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.order = data.order ?? 1;

      this.type = data.type;

      this.subtype = data.subtype ?? 0;

      this.constant = data.constant ?? null;

      this.days_computation = data.days_computation ?? null;

      this.variable = data.variable ?? null;

      this.items = data.items ?? [];

      this.item_tags = data.item_tags ?? [];
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  isOperand(): boolean {
    return this.type == ComputationNodeType.operand;
  }

  isOperator(): boolean {
    return this.type == ComputationNodeType.operator;
  }

  isUnaryOperator(): boolean {
    if (!this.isOperator()) {
      return false;
    }
    switch (this.subtype) {
      case ComputationBinaryOperators.ceil:
      case ComputationBinaryOperators.floor:
      case ComputationBinaryOperators.abs:
        return true;
      default:
        return false;
    }
  }

  isStartBracket(): boolean {
    return Boolean(
      this.type === ComputationNodeType.operator &&
        this.subtype === ComputationBinaryOperators.start_bracket
    );
  }

  isCloseBracket(): boolean {
    return Boolean(
      this.type === ComputationNodeType.operator &&
        this.subtype === ComputationBinaryOperators.close_bracket
    );
  }

  toString(): string {
    let representation = 'Node - ';

    if (this.isOperator()) {
      representation += 'Operator';
      switch (this.subtype) {
        case ComputationBinaryOperators.add:
          representation += ": '+'";
          break;

        case ComputationBinaryOperators.subtract:
          representation += ": '-'";
          break;

        case ComputationBinaryOperators.multiply:
          representation += ": '*'";
          break;

        case ComputationBinaryOperators.divide:
          representation += ": '/'";
          break;

        case ComputationBinaryOperators.exponent:
          representation += ": '^'";
          break;

        case ComputationBinaryOperators.start_bracket:
          representation += ": '('";
          break;

        case ComputationBinaryOperators.close_bracket:
          representation += ": ')'";
          break;

        case ComputationBinaryOperators.ceil:
          representation += ": 'Ceil'";
          break;

        case ComputationBinaryOperators.floor:
          representation += ": 'Floor'";
          break;

        case ComputationBinaryOperators.abs:
          representation += ": 'Abs'";
          break;
      }
    } else {
      representation += 'Operand';
      switch (this.subtype) {
        case ComputationNodeSubtype.constant:
          representation += `:constant: ${this.constant}`;
          break;

        case ComputationNodeSubtype.variable:
          representation += `:variable: ${this.variable}`;
          break;

        case ComputationNodeSubtype.item_quantity:
          representation += `:item_quantity: ${this.items}`;
          break;

        case ComputationNodeSubtype.item_exists:
          representation += `:item_exists: ${this.items}`;
          break;

        case ComputationNodeSubtype.days_computation:
          representation += `:days_computation: ${this.days_computation}`;
          break;

        case ComputationNodeSubtype.item_tag_quantity:
          representation += `:item_tag_quantity: ${this.item_tags}`;
          break;
        case ComputationNodeSubtype.item_tag_exists:
          representation += `:item_tag_exists: ${this.item_tags}`;
          break;
      }
    }

    return representation;
  }
}

export class ServicePriceComputation implements IServicePriceComputation {
  id?: number;
  description?: string = '';
  nodes: IServiceComputationNode[] = [];

  constructor(data?: IServicePriceComputationData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      this.description = data.description ?? '';

      if (data.nodes) {
        this.nodes = data.nodes.map(node => new ServiceComputationNode(node));
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toNodeList(tree: ExpressionNodeTree): IServiceComputationNode[] {
    return convertExpressionTreeToNodeList(tree);
  }

  toExpressionTree(): ExpressionNodeTree {
    return convertNodeListToExpressionTree(this.nodes);
  }

  public static MAX_EXPRESSION_TREE_DEPTH = 5;
}

/**
 *  Pre-order tree traversal visits each node using stack.
 *  Checks if leaf node is not an array otherwise
 *  pushes all nodes into stack and continues traversal.
 *  @param {ExpressionNodeTree} root - expression tree to add
 *  @returns array  - final array of nodes in order
 */
export function convertExpressionTreeToNodeList(root: ExpressionNodeTree) {
  const nodeList: IServiceComputationNode[] = [];

  for (const node of root) {
    if (!Array.isArray(node)) {
      nodeList.push(node);
    } else {
      nodeList.push(...convertExpressionTreeToNodeList(node));
    }
  }

  return nodeList;
}

/**
 *  @param {IServiceComputationNode[]} nodeList - a list of nodes
 *  @returns {ExpressionNodeTree} tree  - tree structure of an array of nodes or nodes[]
 */
export function convertNodeListToExpressionTree(
  nodes: IServiceComputationNode[],
  index = 0,
  initialNode?: IServiceComputationNode
) {
  const tree: ExpressionNodeTree = [];

  if (initialNode) {
    tree.push(initialNode);
  }
  let nodeIndex = index;
  while (nodeIndex < nodes.length) {
    const node = nodes[nodeIndex];

    if (node.isStartBracket()) {
      //TODO: - LH - look into if result tree should return (length:number, ExpressionNodeTree)
      const resultTree = convertNodeListToExpressionTree(
        nodes,
        nodeIndex + 1, //index to start sub group
        node
      );

      tree.push(resultTree);

      nodeIndex++;

      // get total flattened arrays length
      nodeIndex += resultTree.flat(6).length - 1;
    } else {
      tree.push(node);
      nodeIndex++;
      if (node.isCloseBracket()) {
        return tree;
      }
    }
  }

  return tree;
}

export class ServiceComputationNodeFieldError
  implements IServiceComputationNodeFieldError {
  readonly order: string[] = [];
  readonly type: string[] = [];
  readonly subtype: string[] = [];
  readonly constant: string[] = [];
  readonly question: string[] = [];
  readonly items: string[] = [];
  readonly variable: string[] = [];
  readonly days_computation: string[] = [];
  readonly item_tags: string[] = [];

  constructor(data?: IServiceComputationNodeFieldErrorData) {
    if (data) {
      this.order = data.order ?? [];
      this.type = data.type ?? [];
      this.subtype = data.subtype ?? [];
      this.constant = data.constant ?? [];
      this.question = data.question ?? [];
      this.items = data.items ?? [];
      this.variable = data.variable ?? [];
      this.days_computation = data.days_computation ?? [];
      this.item_tags = data.item_tags ?? [];
    }
  }

  clearErrors(): void {
    this.order.splice(0, this.order.length);
    this.type.splice(0, this.type.length);
    this.subtype.splice(0, this.subtype.length);
    this.constant.splice(0, this.constant.length);
    this.question.splice(0, this.question.length);
    this.items.splice(0, this.items.length);
    this.variable.splice(0, this.variable.length);
    this.days_computation.splice(0, this.days_computation.length);
    this.item_tags.splice(0, this.item_tags.length);
  }

  static isServiceComputationNodeFieldError(
    data: IServiceComputationNodeFieldErrorData
  ): boolean {
    return false;
  }
}
export class ServiceComputationFieldError
  implements IServiceComputationFieldError {
  description: string[] = [];
  nodes: IServiceComputationNodeFieldError[] = [];

  constructor(data?: IServiceComputationFieldErrorData) {
    if (data) {
      this.description = data.description ?? [];
      this.nodes = (data.nodes ?? []).map(
        node => new ServiceComputationNodeFieldError(node)
      );
    }
  }
  clearErrors(): void {
    this.description.splice(0, this.description.length);
    this.nodes.splice(0, this.nodes.length);
  }
  static isQuoteServiceComputationFieldError(
    data: IServiceComputationFieldErrorData
  ): boolean {
    return false;
  }
}
